import { Drawer } from '@economic/taco';
import { Trans, t } from '@lingui/macro';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Authorise } from '@economic/react-app';
import Intercom from '~/app/shared/components/Intercom';
import { HelpDrawerActions } from '~/app/components/Header/Help/HelpDrawerActions';
import { useHelpDrawer } from '~/app/components/Header/Help/HelpDrawerContext';
import { ApplaiView } from '~/app/components/Header/Help/ai-chat/ApplaiView';
import { MailView } from '~/app/components/Header/Help/mail/MailView';
import { DrilldownButton } from '~/app/components/Header/Help/overview/DrilldownButton';
import { LinkButton } from '~/app/components/Header/Help/overview/LinkButton';
import { PhoneView } from '~/app/components/Header/Help/phone/PhoneView';

export function HelpOverview(): React.JSX.Element {
    const chatWithOurAiChatbot = t`AI Chat`;
    const { navigate } = useHelpDrawer();
    const { pathname } = useLocation();

    // Hardcoded path for user testing purposes to only show the alternative menu style for this specific page
    const showOldMenuVariant = pathname === '/sales/customers';

    return (
        <>
            <Drawer.Title>
                {t`Help`}
                <HelpDrawerActions />
            </Drawer.Title>
            <div className="p-4 overflow-auto flex-1">
                <ul className="flex flex-col gap-2">
                    <li>
                        <DrilldownButton
                            icon="ai-chat-solid"
                            description={
                                <Trans>
                                    Expected response time: <b>0min</b>
                                </Trans>
                            }
                            header={chatWithOurAiChatbot}
                            onClick={() => navigate(<ApplaiView title={chatWithOurAiChatbot} />)}
                            showNextIcon
                        />
                    </li>
                    <Authorise path="chat">
                        <li>
                            <Intercom>
                                {({ toggle }) => (
                                    <DrilldownButton
                                        header={t`Support Chat`}
                                        description={
                                            <Trans>
                                                Expected response time: <b>15min</b>
                                            </Trans>
                                        }
                                        icon="chat-solid"
                                        onClick={() => toggle()}
                                        showNextIcon
                                    />
                                )}
                            </Intercom>
                        </li>
                    </Authorise>
                    <li>
                        <DrilldownButton
                            header={<Trans id="HELP_PHONE">Phone</Trans>}
                            description={
                                <Trans>
                                    Expected response time: <b>40min</b>
                                </Trans>
                            }
                            icon="phone-solid"
                            onClick={() => navigate(<PhoneView />)}
                            showNextIcon
                        />
                    </li>
                    <li>
                        <DrilldownButton
                            header={t`Mail`}
                            description={
                                <Trans>
                                    Expected response time: <b>Within 48 hours</b>
                                </Trans>
                            }
                            icon="envelope-solid"
                            onClick={() => navigate(<MailView />)}
                            showNextIcon
                        />
                    </li>
                    <li>
                        <DrilldownButton
                            header={t`e-copedia`}
                            description={t`Find help articles for e-conomic`}
                            icon="e-copedia"
                            onClick={() => window.open('https://www.e-conomic.dk/support/e-copedia', '_blank')}
                        />
                    </li>
                    {showOldMenuVariant ? null : (
                        <li>
                            <DrilldownButton
                                header={t`Privacy policy`}
                                description={t`Our policy for protecting your information`}
                                icon="e-copedia"
                                onClick={() => window.open('https://www.e-conomic.dk/support/e-copedia', '_blank')}
                            />
                        </li>
                    )}
                </ul>
                {showOldMenuVariant ? (
                    <ul className="flex flex-col">
                        <li>
                            <LinkButton
                                icon="play"
                                title={t`Help videos`}
                                link="https://www.e-conomic.dk/support/e-copedia/hjaelpevideoer"
                                mode="external"
                            />
                        </li>
                        <li>
                            <LinkButton
                                icon="courses"
                                title={t`Online courses`}
                                link="https://www.e-conomic.dk/support/online-live-kursus"
                                mode="external"
                            />
                        </li>
                        <li>
                            <LinkButton
                                icon="book"
                                title={t`Accounting dictionary`}
                                link="https://www.e-conomic.dk/regnskabsprogram/ordbog"
                                mode="external"
                            />
                        </li>
                        <li>
                            <LinkButton
                                icon="star"
                                title={t`Latest updates in e-conomic`}
                                link="https://www.e-conomic.dk/support/forbedringer"
                                mode="embedded"
                            />
                        </li>
                        <li>
                            <LinkButton
                                icon="person-change"
                                title={t`Support options`}
                                link="https://www.e-conomic.dk/support"
                                mode="embedded"
                            />
                        </li>
                        <li>
                            <LinkButton
                                icon="document"
                                title={t`Privacy policy`}
                                link="https://www.e-conomic.dk/sikkerhed/privacy"
                                mode="embedded"
                            />
                        </li>
                    </ul>
                ) : null}
            </div>
        </>
    );
}
